// Form üstündeki "Bu reklam neye benzer" canlı özet kartı.
// Form değiştikçe re-render olur, kullanıcı seçimlerinin anlamını insan diliyle anlatır.

import {
  AGE_BRACKET_LABELS_TR,
  GENDER_LABELS_TR,
  GROUP_TYPE_LABELS_TR,
  ORIENTATION_LABELS_TR,
} from "../constants.js";
import { priorityDescription } from "../scoring.js";

function escapeHtml(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function describeAges(brackets) {
  if (!brackets.length) return { line: "Tüm yaşlar", note: "Yaş hedeflemesi yok — herkese çıkar" };
  const labels = brackets.map((b) => AGE_BRACKET_LABELS_TR[b] || b);
  return { line: labels.join(", "), note: `${brackets.length} yaş aralığı seçili` };
}

function describeGenders(genders) {
  if (!genders.length) return { line: "Tüm cinsiyetler", note: "Cinsiyet hedeflemesi yok" };
  if (genders.length === 2) return { line: "Erkek + Kadın", note: "İkisi de hedef" };
  return {
    line: GENDER_LABELS_TR[genders[0]] || genders[0],
    note: `Sadece ${GENDER_LABELS_TR[genders[0]] || genders[0]} izleyicide çıkar`,
  };
}

function describeGroups(groups) {
  if (!groups.length)
    return { line: "Tüm gruplar", note: "Grup kompozisyonu hedeflemesi yok" };
  const labels = groups.map((g) => GROUP_TYPE_LABELS_TR[g] || g);
  return {
    line: labels.join(", "),
    note: `Sadece ${labels.length} grup tipinde çıkar`,
  };
}

function describeOrientation(o) {
  const note = {
    any: "Yatay + dikey kioska sığar",
    portrait: "Dikey kioska özel — yatayda siyah barlarla gösterilir",
    landscape: "Yatay kioska özel — dikeyde siyah barlarla gösterilir",
  };
  return {
    line: ORIENTATION_LABELS_TR[o] || o,
    note: note[o] || "",
  };
}

function describeSchedule(start, end) {
  if (start === 0 && end === 24) return { line: "24 saat", note: "Her saat çıkabilir" };
  const pad = (n) => String(n).padStart(2, "0");
  if (start < end) {
    return { line: `${pad(start)}:00 — ${pad(end)}:00`, note: `${end - start} saat aralığında çıkar` };
  }
  return { line: `${pad(start)}:00 — ${pad(end)}:00 (gece aşımı)`, note: "Gece yarısını aşan aralık" };
}

function describeFreqCap(cap) {
  if (!cap) return { line: "Sınırsız", note: "Gün içinde kaç kez gösterileceği serbest" };
  return { line: `Günde max ${cap}`, note: "Limit dolunca havuzdan çıkar (rolling 24h)" };
}

function describeDuration(sec) {
  if (sec <= 8) return { line: `${sec} sn`, note: "Çok kısa — hızlı geçer" };
  if (sec <= 20) return { line: `${sec} sn`, note: "Standart kiosk reklamı uzunluğu" };
  if (sec <= 45) return { line: `${sec} sn`, note: "Uzun — izleyici dikkati zorlanır" };
  return { line: `${sec} sn`, note: "Çok uzun — sadece özel kampanyalar için" };
}

function row(label, value, note, accent = "cyan") {
  const accentClass = {
    cyan: "text-cyan-300",
    amber: "text-amber-300",
    emerald: "text-emerald-300",
    rose: "text-rose-300",
  }[accent];
  return `
    <div class="grid grid-cols-[110px_1fr] gap-2 items-baseline">
      <div class="text-[10px] uppercase tracking-widest text-slate-500">${escapeHtml(label)}</div>
      <div>
        <div class="text-xs font-bold ${accentClass}">${escapeHtml(value)}</div>
        ${note ? `<div class="text-[10px] text-slate-500 italic">${escapeHtml(note)}</div>` : ""}
      </div>
    </div>`;
}

export function renderInsightsHtml() {
  return `
    <aside id="insights-card" class="bg-slate-900/60 border border-cyan-500/20 rounded-lg p-4 space-y-2.5">
      <div class="flex items-center justify-between border-b border-slate-800 pb-1.5">
        <h4 class="text-xs uppercase tracking-widest text-cyan-400 font-bold">📋 Bu reklam neye benzer</h4>
        <span class="text-[9px] text-slate-500 italic">canlı güncellenir</span>
      </div>
      <div id="insights-body" class="space-y-2"></div>
    </aside>`;
}

export function updateInsights(formEl) {
  const body = document.getElementById("insights-body");
  if (!body || !formEl) return;

  const fd = new FormData(formEl);
  const title = (fd.get("title") || "").toString().trim();
  const duration = parseInt(fd.get("duration_sec"), 10) || 15;
  const priority = parseInt(fd.get("priority"), 10) || 5;
  const startH = parseInt(fd.get("start_hour"), 10) || 0;
  const endH = parseInt(fd.get("end_hour"), 10);
  const endHour = Number.isFinite(endH) ? endH : 24;
  const cap = fd.get("max_impressions_per_day")
    ? parseInt(fd.get("max_impressions_per_day"), 10)
    : null;
  const orient = fd.get("orientation") || "any";

  const ages = fd.getAll("age_brackets");
  const genders = fd.getAll("genders");
  const groups = fd.getAll("group_types");

  const o = describeOrientation(orient);
  const a = describeAges(ages);
  const g = describeGenders(genders);
  const grp = describeGroups(groups);
  const d = describeDuration(duration);
  const s = describeSchedule(startH, endHour);
  const f = describeFreqCap(cap);
  const p = priorityDescription(priority);

  const targetSummary = (() => {
    if (!ages.length && !genders.length && !groups.length) {
      return {
        line: "Hedefsiz",
        note: "Genel havuza düşer — herkese çıkabilir (varsayılan)",
        accent: "amber",
      };
    }
    const parts = [];
    if (ages.length) parts.push(`${ages.length} yaş`);
    if (genders.length) parts.push(genders.length === 1 ? GENDER_LABELS_TR[genders[0]] : "Erkek+Kadın");
    if (groups.length) parts.push(`${groups.length} grup`);
    return { line: parts.join(" · "), note: "Sadece bu kitleye çıkar", accent: "emerald" };
  })();

  body.innerHTML = [
    row("Başlık", title || "—", title ? "" : "Henüz girilmedi", title ? "cyan" : "rose"),
    row("Süre", d.line, d.note),
    row("Yön", o.line, o.note),
    row("Hedef", targetSummary.line, targetSummary.note, targetSummary.accent),
    row("Yaş", a.line, a.note),
    row("Cinsiyet", g.line, g.note),
    row("Grup", grp.line, grp.note),
    row("Öncelik", `${priority}/10 (${p.label})`, p.note, priority >= 9 ? "rose" : priority >= 7 ? "amber" : "cyan"),
    row("Saat", s.line, s.note),
    row("Limit", f.line, f.note),
  ].join("");
}
